import { useEffect, useState } from 'react';
import { fetchSourceFile, sourceFileUrl } from './api';
import { labels } from './labels';
import { LoadError } from './load-error';

type ViewerState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'missing' }
  | { status: 'unsupported' }
  | { status: 'tooLarge' }
  | { status: 'ok'; url: string; kind: 'pdf' | 'image' };

/**
 * QA-163: the stored source file of a document (`GET /api/doc/file`), shown in the console. It
 * mounts when the operator asks for the file, so it reads the file once for each opening. A PDF
 * shows in an `<iframe>` (the browser's own viewer) and an image in an `<img>`, from an object URL of
 * the bytes read here. **Open in a new tab** links to the same file on the API.
 */
export function SourceFileViewer({ docId }: { docId: string }) {
  const text = labels.sourceFile;
  const [state, setState] = useState<ViewerState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let url: string | undefined;
    setState({ status: 'loading' });
    setBroken(false);
    fetchSourceFile(docId, controller.signal)
      .then((file) => {
        if (controller.signal.aborted) return;
        if (file.status !== 'ok') {
          setState({ status: file.status });
          return;
        }
        url = URL.createObjectURL(file.blob);
        setState({ status: 'ok', url, kind: file.blob.type === 'application/pdf' ? 'pdf' : 'image' });
      })
      .catch(() => {
        if (!controller.signal.aborted) setState({ status: 'error' });
      });
    return () => {
      controller.abort();
      if (url) URL.revokeObjectURL(url);
    };
  }, [docId, attempt]);

  return (
    <div className="file-viewer">
      {state.status === 'loading' && <p className="notice">{text.loading}</p>}
      {state.status === 'missing' && <p className="notice">{text.missing}</p>}
      {state.status === 'unsupported' && <p className="notice">{text.unsupported}</p>}
      {state.status === 'tooLarge' && <p className="notice">{text.tooLarge}</p>}
      {state.status === 'error' && (
        <LoadError text={text.loadFailed} onRetry={() => setAttempt((count) => count + 1)} />
      )}
      {state.status === 'ok' && (
        <>
          {broken && <p className="notice">{text.cannotShow}</p>}
          {state.kind === 'pdf' ? (
            <iframe className="file-viewer__frame" src={state.url} title={text.frameTitle(docId)} />
          ) : (
            !broken && (
              <img
                className="file-viewer__image"
                src={state.url}
                alt={text.frameTitle(docId)}
                onError={() => setBroken(true)}
              />
            )
          )}
          <a className="file-viewer__open" href={sourceFileUrl(docId)} target="_blank" rel="noopener noreferrer">
            {text.openInNewTab}
          </a>
        </>
      )}
    </div>
  );
}
